import React, {useState} from 'react'

import {useParams} from 'react-router-dom'
import {FormattedMessage, useIntl} from 'react-intl'

import {useProductList} from '../util/product-list-context'
import {PRODUCT_LIST_TOTAL} from '../../product-list'

import {Box, Flex, Text, Grid, Heading} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AbsoluteCenter, Skeleton} from '@chakra-ui/react'
import DynamicImage from '@app/components/dynamic-image'
import Link from '../../../components/link'

import PageHeader from './page-header'
import LinkedCategories from './linked-categories'

// Custom Components
import MetaTagsComponent from '../../../components/metatags'

// Custom utils
import {buildMetaData} from '../../../components/metatags/util/metaDataUtils'
import {isHydrated} from '../../../utils/utils'

const WIDTHS_DIS_MAPPING = {
    sm: {
        1: 768, // 1 tile in a row
        2: 384 // 2 tiles in a row
    },
    md: {
        2: 600, // 2 tiles in a row
        3: 400 // 3 tiles in a row
    },
    lg: {
        2: 720, // 2 tiles in a row
        3: 480 // 3 tiles in a row
    },
    xl: {
        2: 960, // 2 tiles in a row
        3: 640 // 3 tiles in a row
    },
    '2xl': {
        2: 1260, // 2 tiles in a row
        3: 840 // 3 tiles in a row
    }
}

const MOSAIC_GRID = {
    5: {
        // Mobile (5 tiles): 2 + 2 + 1 => 1 row - 2 tiles, 2 row - 2 tiles, 3 row - 1 tile
        base: {
            templateColumns: 'repeat(2, 1fr)',
            gridTemplateAreas: `
                "tile1 tile2"
                "tile3 tile4"
                "tile5 tile5"
            `,
            size: ['4xl', '4xl', '4xl', '4xl', '5xl'],
            widthMapping: [2, 2, 2, 2, 1]
        },
        // From Tablet (5 tiles): 3 + 2 => 1 row - 3 tiles, 2 row - 2 tiles
        md: {
            templateColumns: 'repeat(6, 1fr)',
            gridTemplateAreas: `
                "tile1 tile1 tile2 tile2 tile3 tile3"
                "tile4 tile4 tile4 tile5 tile5 tile5"
            `,
            size: ['4xl', '4xl', '4xl', '5xl', '5xl'],
            widthMapping: [3, 3, 3, 2, 2]
        },
        lg: {
            size: ['5xl', '5xl', '5xl', '6xl', '6xl']
        }
    },
    6: {
        // Mobile (6 tiles): 2 + 1 + 2 + 1 => 1 and 3 rows - 2 tiles, 2 and 4 rows - 1 tile
        base: {
            templateColumns: 'repeat(2, 1fr)',
            gridTemplateAreas: `
                "tile1 tile2"
                "tile3 tile3"
                "tile4 tile5"
                "tile6 tile6"
            `,
            size: ['4xl', '4xl', '5xl', '4xl', '4xl', '5xl'],
            widthMapping: [2, 2, 1, 2, 2, 1]
        },
        // From Tablet (6 tiles): 3 + 3: 1 and 2 rows - 3 tiles
        md: {
            templateColumns: 'repeat(3, 1fr)',
            gridTemplateAreas: `
                "tile1 tile2 tile3"
                "tile4 tile5 tile6"
            `,
            size: ['4xl', '4xl', '4xl', '4xl', '4xl', '4xl'],
            widthMapping: [3, 3, 3, 3, 3, 3]
        },
        lg: {
            size: ['5xl', '5xl', '5xl', '5xl', '5xl', '5xl']
        }
    },
    7: {
        // Mobile (7 tiles): 2 + 2 + 1 + 2
        base: {
            templateColumns: 'repeat(2, 1fr)',
            gridTemplateAreas: `
                "tile1 tile2"
                "tile3 tile4"
                "tile5 tile5"
                "tile6 tile7"
            `,
            size: ['4xl', '4xl', '4xl', '4xl', '5xl', '4xl', '4xl'],
            widthMapping: [2, 2, 2, 2, 1, 2, 2]
        },
        // From Tablet (7 tiles): 2 + 3 + 2
        md: {
            templateColumns: 'repeat(6, 1fr)',
            gridTemplateAreas: `
                "tile1 tile1 tile1 tile2 tile2 tile2"
                "tile3 tile3 tile4 tile4 tile5 tile5"
                "tile6 tile6 tile6 tile7 tile7 tile7"
            `,
            size: ['5xl', '5xl', '4xl', '4xl', '4xl', '5xl', '5xl'],
            widthMapping: [2, 2, 3, 3, 3, 2, 2]
        },
        lg: {
            size: ['6xl', '6xl', '5xl', '5xl', '5xl', '6xl', '6xl']
        }
    },
    8: {
        // Mobile (8 tiles): 2 + 2 + 1 + 2 + 1
        base: {
            templateColumns: 'repeat(2, 1fr)',
            gridTemplateAreas: `
                "tile1 tile2"
                "tile3 tile4"
                "tile5 tile5"
                "tile6 tile7"
                "tile8 tile8"
            `,
            size: ['4xl', '4xl', '4xl', '4xl', '5xl', '4xl', '4xl', '5xl'],
            widthMapping: [2, 2, 2, 2, 1, 2, 2, 1]
        },
        // Tablet (8 tiles): 3 + 2 + 3
        md: {
            templateColumns: 'repeat(6, 1fr)',
            gridTemplateAreas: `
                "tile1 tile1 tile2 tile2 tile3 tile3"
                "tile4 tile4 tile4 tile5 tile5 tile5"
                "tile6 tile6 tile7 tile7 tile8 tile8"
            `,
            size: ['4xl', '4xl', '4xl', '5xl', '5xl', '4xl', '4xl', '4xl'],
            widthMapping: [3, 3, 3, 2, 2, 3, 3, 3]
        },
        lg: {
            size: ['5xl', '5xl', '5xl', '6xl', '6xl', '5xl', '5xl', '5xl']
        }
    },
    9: {
        // Mobile (9 tiles): 2 + 2 + 1 + 2 + 2
        base: {
            templateColumns: 'repeat(2, 1fr)',
            gridTemplateAreas: `
                "tile1 tile2"
                "tile3 tile4"
                "tile5 tile5"
                "tile6 tile7"
                "tile8 tile9"
            `,
            size: ['4xl', '4xl', '4xl', '4xl', '5xl', '4xl', '4xl', '4xl', '4xl'],
            widthMapping: [2, 2, 2, 2, 1, 2, 2, 2, 2]
        },
        // From Tablet (9 tiles): 2 + 3 + 2 + 2
        md: {
            templateColumns: 'repeat(6, 1fr)',
            gridTemplateAreas: `
                "tile1 tile1 tile1 tile2 tile2 tile2"
                "tile3 tile3 tile4 tile4 tile5 tile5"
                "tile6 tile6 tile6 tile7 tile7 tile7"
                "tile8 tile8 tile8 tile9 tile9 tile9"
            `,
            size: ['5xl', '5xl', '4xl', '4xl', '4xl', '5xl', '5xl', '5xl', '5xl'],
            widthMapping: [2, 2, 3, 3, 3, 2, 2, 2, 2]
        },
        lg: {
            size: ['6xl', '6xl', '5xl', '5xl', '5xl', '6xl', '6xl', '6xl', '6xl']
        }
    },
    10: {
        // Mobile (10 tiles): 2 + 2 + 1 + 2 + 2 + 1
        base: {
            templateColumns: 'repeat(2, 1fr)',
            gridTemplateAreas: `
                "tile1 tile2"
                "tile3 tile4"
                "tile5 tile5"
                "tile6 tile7"
                "tile8 tile9"
                "tile10 tile10"
            `,
            size: ['4xl', '4xl', '4xl', '4xl', '5xl', '4xl', '4xl', '4xl', '4xl', '5xl'],
            widthMapping: [2, 2, 2, 2, 1, 2, 2, 2, 2, 1]
        },
        // From Tablet (10 tiles): 3 + 2 + 3 + 2
        md: {
            templateColumns: 'repeat(6, 1fr)',
            gridTemplateAreas: `
                "tile1 tile1 tile2 tile2 tile3 tile3"
                "tile4 tile4 tile4 tile5 tile5 tile5"
                "tile6 tile6 tile7 tile7 tile8 tile8"
                "tile9 tile9 tile9 tile10 tile10 tile10"
            `,
            size: ['4xl', '4xl', '4xl', '5xl', '5xl', '4xl', '4xl', '4xl', '5xl', '5xl'],
            widthMapping: [3, 3, 3, 2, 2, 3, 3, 3, 2, 2]
        },
        lg: {
            size: ['5xl', '5xl', '5xl', '6xl', '6xl', '5xl', '5xl', '5xl', '6xl', '6xl']
        }
    }
}

const ProductListMosaic = () => {
    const {formatMessage} = useIntl()
    const {category, total, isProductListLoading, metatags} = useProductList()
    const params = useParams()
    const [isImageLoaded, setIsImageLoaded] = useState(false)

    const mosaicData = Array.from({length: 10}, (_, i) => {
        const index = i + 1
        const image = category[`c_mosaicImage${index}`]
        const link = category[`c_mosaicLink${index}`]
        const title = category[`c_mosaicTitle${index}`]

        if (image && link && title) {
            return {
                image,
                link,
                title,
                ...(category[`c_mosaicImageAlt${index}`] && {
                    alt: category[`c_mosaicImageAlt${index}`]
                }),
                ...(category[`c_mosaicImageTitle${index}`] && {
                    imageTitle: category[`c_mosaicImageTitle${index}`]
                })
            }
        }
    }).filter(Boolean)

    const mosaicTileAmount = mosaicData.length

    /*
     * Metadata constructed in the getProps method and passed to the MetaTags component.
     */
    const finalMetadata = buildMetaData(category, metatags)

    return (
        <>
            <MetaTagsComponent metaData={finalMetadata} />
            {/* Header */}
            <PageHeader
                containerProps={{alignItems: 'center'}}
                descriptionProps={{maxWidth: {base: '330px', md: '590px'}, textAlign: 'center'}}
            />

            <Box as="section">
                {/* Top PLP panel */}
                <Flex
                    direction="row"
                    justify="space-between"
                    align="center"
                    gap="30px"
                    overflowX="auto"
                    whiteSpace="nowrap"
                    pb={9}
                    m={[10, null, '13px 20px 4px']}
                >
                    {/* Linked categories List */}
                    <LinkedCategories
                        categories={category?.c_linkedCategories}
                        containerProps={{m: '0', p: '0', overflowX: 'none'}}
                    />

                    {/* Total */}
                    {!isProductListLoading && (
                        <Text variant="bodySmall" color="darkGray" whiteSpace="nowrap">
                            {formatMessage(PRODUCT_LIST_TOTAL, {total: total || 0})}
                        </Text>
                    )}
                </Flex>

                {/* Mosaic Grid: min 5 mosaic tiles on the page */}
                {mosaicTileAmount > 4 && (
                    <Grid
                        as="ul"
                        listStyleType="none"
                        templateColumns={{
                            base: MOSAIC_GRID[mosaicTileAmount].base.templateColumns,
                            md: MOSAIC_GRID[mosaicTileAmount].md.templateColumns
                        }}
                        gridTemplateAreas={{
                            base: MOSAIC_GRID[mosaicTileAmount].base.gridTemplateAreas,
                            md: MOSAIC_GRID[mosaicTileAmount].md.gridTemplateAreas
                        }}
                        gap="10px"
                    >
                        {mosaicData.map((tile, index) => {
                            // heading size mapping
                            const headingSizeBase =
                                MOSAIC_GRID[mosaicTileAmount].base.size[index] || '4xl'
                            const headingSizeMd =
                                MOSAIC_GRID[mosaicTileAmount].md.size[index] || '4xl'
                            const headingSizeLg =
                                MOSAIC_GRID[mosaicTileAmount].lg.size[index] || '5xl'

                            // Get tile row layout
                            const baseTileLayout =
                                MOSAIC_GRID[mosaicTileAmount].base.widthMapping[index]
                            const mdTileLayout =
                                MOSAIC_GRID[mosaicTileAmount].md.widthMapping[index]

                            return (
                                <Box
                                    key={`${tile.image}-${index}`}
                                    gridArea={`tile${index + 1}`}
                                    as="li"
                                >
                                    {isHydrated() && tile && tile.image ? (
                                        <Link
                                            href={tile.link}
                                            display="block"
                                            variant="noUnderline"
                                            position="relative"
                                            height="100%"
                                        >
                                            {
                                                // Prevent CLS while image is loading
                                                !isImageLoaded && index < 5 && (
                                                    <Skeleton
                                                        aspectRatio="59 / 74"
                                                        width={'100%'}
                                                    />
                                                )
                                            }
                                            <DynamicImage
                                                src={`${tile.image}[?sw={width}]`}
                                                widths={[
                                                    WIDTHS_DIS_MAPPING.sm[baseTileLayout],
                                                    WIDTHS_DIS_MAPPING.sm[baseTileLayout],
                                                    WIDTHS_DIS_MAPPING.md[mdTileLayout],
                                                    WIDTHS_DIS_MAPPING.lg[mdTileLayout],
                                                    WIDTHS_DIS_MAPPING.xl[mdTileLayout],
                                                    WIDTHS_DIS_MAPPING['2xl'][mdTileLayout]
                                                ]}
                                                imageProps={{
                                                    width: '100%',
                                                    loading: 'eager',
                                                    fetchpriority: 'high',
                                                    alt: tile.alt || '',
                                                    title: tile.imageTitle || '',
                                                    onLoad: () => setIsImageLoaded(true),
                                                    display:
                                                        isImageLoaded || index >= 5
                                                            ? 'block'
                                                            : 'none'
                                                }}
                                            />
                                            <AbsoluteCenter
                                                width="full"
                                                padding={{base: '10px', lg: '20px'}}
                                                display={
                                                    isImageLoaded || index >= 5 ? 'block' : 'none'
                                                }
                                            >
                                                <Heading
                                                    as="h2"
                                                    size={{
                                                        base: headingSizeBase,
                                                        md: headingSizeMd,
                                                        lg: headingSizeLg
                                                    }}
                                                    color="white"
                                                    textTransform="uppercase"
                                                    dangerouslySetInnerHTML={{
                                                        __html: tile.title
                                                    }}
                                                />
                                            </AbsoluteCenter>
                                        </Link>
                                    ) : (
                                        <Skeleton aspectRatio="59 / 74" width={'100%'} />
                                    )}
                                </Box>
                            )
                        })}
                    </Grid>
                )}

                {/* PLP Bottom  */}
                <Flex
                    justify="center"
                    px={{base: '10px', lg: '20px'}}
                    py={{base: '70px', lg: '80px'}}
                >
                    <Link
                        variant="secondary"
                        size="md"
                        whiteSpace="nowrap"
                        href={`/category/${params.categoryId}`}
                        useNavLink
                    >
                        <FormattedMessage
                            defaultMessage="View all {totalCount}"
                            id="product_list.mosaic.shop_all"
                            values={{
                                totalCount: !isProductListLoading && (
                                    <Text as="span" color="darkGray" paddingLeft="3px">
                                        ({total || 0} products)
                                    </Text>
                                )
                            }}
                        />
                    </Link>
                </Flex>
            </Box>
        </>
    )
}

ProductListMosaic.propTypes = {}

export default ProductListMosaic
